/* eslint-disable react/require-default-props */
import { Box, Checkbox, IconButton, MenuItem, Select, useMediaQuery, useTheme } from '@mui/material';
import {
  CellContext,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Header,
  PaginationState,
  Row,
  RowData,
  SortingState,
  Table,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PAGINATION__OF,
  PAGINATION__PAGE,
  PAGINATION__ROWS,
  PAGINATION__SHOWING,
  PAGINATION__TO,
} from 'translations/constants';
import { ReactComponent as SortArrow } from '../../assets/icons/SortArrowIcon.svg';
import {
  CheckboxCell,
  EmptyStateContainer,
  MobileCard,
  MobileCardActions,
  MobileCardContainer,
  MobileCardLabel,
  MobileCardRow,
  MobileCardValue,
  PaginationContainer,
  PaginationControls,
  PaginationInfo,
  SelectionFooterContainer,
  StyledTable,
  StyledTbody,
  StyledTd,
  StyledTh,
  StyledThead,
  StyledTr,
  TableContainerStyled,
} from './styles';
import { TableFooter } from './table-footer';
import { UPTanStackTableColumn, UPTanStackTableProps } from './types';

// Type definition for column meta data
interface ColumnMeta {
  align?: 'left' | 'center' | 'right';
  headerAlign?: 'left' | 'center' | 'right';
  colSpan?: (row: unknown) => number;
}

interface HeaderCheckboxProps<TData extends RowData> {
  table: Table<TData>;
  customCheckbox?: { checked: boolean; indeterminate?: boolean; onChange: () => void; testId?: string };
}

const HeaderCheckbox = <TData extends RowData>({ table, customCheckbox }: HeaderCheckboxProps<TData>) => {
  const handleChange = customCheckbox?.onChange ?? table.getToggleAllRowsSelectedHandler();

  return (
    <CheckboxCell>
      <Checkbox
        checked={customCheckbox?.checked ?? table.getIsAllRowsSelected()}
        indeterminate={customCheckbox?.indeterminate ?? table.getIsSomeRowsSelected()}
        onChange={handleChange}
        size="medium"
        inputProps={{ 'data-testid': customCheckbox?.testId } as React.InputHTMLAttributes<HTMLInputElement>}
      />
    </CheckboxCell>
  );
};

const RowCheckbox = <TData extends RowData>({
  row,
  disableSelectionOnClick,
}: {
  row: Row<TData>;
  disableSelectionOnClick: boolean;
}) => (
  <CheckboxCell>
    <Checkbox
      checked={row.getIsSelected()}
      onChange={row.getToggleSelectedHandler()}
      size="medium"
      onClick={e => disableSelectionOnClick && e.stopPropagation()}
    />
  </CheckboxCell>
);

const UPTanStackTable = <TData extends RowData>(props: UPTanStackTableProps<TData>): JSX.Element => {
  const {
    // Data
    data,
    columns: userColumns,
    getRowId,
    emptyMessage = 'No data available',

    // Sorting (Controlled or Uncontrolled)
    sorting: controlledSorting,
    onSortingChange: onSortingChangeCallback,
    enableSorting = true,
    manualSorting = false,

    // Pagination (Controlled or Uncontrolled)
    pagination: controlledPagination,
    onPaginationChange: onPaginationChangeCallback,
    enablePagination = true,
    initialPageSize = 25,
    pageSizeOptions = [25, 50, 100],
    showPaginationControls = true,
    manualPagination = false,
    pageCount,
    totalItems,
    autoResetPageIndex,
    scrollToRowId,

    // Row Selection
    enableRowSelection = false,
    withCheckboxes,
    selectedRows = [],
    onSelectionChange,
    disableSelectionOnClick = false,
    customHeaderCheckbox,

    // Row Hover
    setRowOnHover,
    onRowHover,

    // Selection Footer
    renderSelectionFooter,
    footerActions,
    footerClearAllText = 'Clear all',
    footerSelectedText = 'selected',
    footerOfText = 'of',
    footerCanClearAll = true,
    footerExtraText,
    footerSelectedCount,
    footerSelectedCountTestId,
    footerTotalCount,

    // Mobile Card View
    mobileBreakpoint = 768,
    renderMobileCard,

    // Styling
    sx,
    testId,
    getRowTestId,
    headerHeight,
    getRowSeverity,
    getRowClassName,
    getRowHeight,

    // Legacy props
    hideFooter = false,
    experimentalFeatures = null,
    onCellEditStop = null,
    selectionModel,
    sortModel,
    handleSortModelChange,
    onHeaderColumnClick,
    onPageSizeChange,
    onPageChange,
    onCellEditCommit,
  } = props;

  // Detect mobile view
  const theme = useTheme();
  const [t] = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));

  // Support both enableRowSelection and withCheckboxes for backward compatibility
  const shouldShowCheckboxes = enableRowSelection || withCheckboxes;

  // Hover state (always internal)
  const [hoveredRowId, setHoveredRowId] = useState<string | number | null>(null);

  // Footer animation state
  const [showFooter, setShowFooter] = useState<boolean>(false);
  const hasSelectedRows = selectedRows.length > 0;

  // Animate footer visibility when selection changes
  useEffect(() => {
    if (hasSelectedRows) {
      // Small delay to trigger the animation
      const timer = setTimeout(() => setShowFooter(true), 10);
      return () => clearTimeout(timer);
    }
    setShowFooter(false);
    return undefined;
  }, [hasSelectedRows]);

  // Sorting state (Controlled or Uncontrolled)
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const sorting = controlledSorting ?? internalSorting;
  const setSorting = useCallback(
    (updater: React.SetStateAction<SortingState>) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      if (controlledSorting === undefined) {
        setInternalSorting(newSorting);
      }
      onSortingChangeCallback?.(newSorting);
    },
    [controlledSorting, onSortingChangeCallback, sorting],
  );

  // Pagination state (Controlled or Uncontrolled)
  const [internalPagination, setInternalPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize || 25,
  });
  const pagination = controlledPagination ?? internalPagination;
  const setPagination = useCallback(
    (updater: React.SetStateAction<PaginationState>) => {
      const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
      if (controlledPagination === undefined) {
        setInternalPagination(newPagination);
      }
      onPaginationChangeCallback?.(newPagination);

      // Legacy callbacks
      if (newPagination.pageIndex !== pagination.pageIndex) {
        onPageChange?.(newPagination.pageIndex);
      }
      if (newPagination.pageSize !== pagination.pageSize) {
        onPageSizeChange?.(newPagination.pageSize);
      }
    },
    [controlledPagination, onPaginationChangeCallback, pagination, onPageChange, onPageSizeChange],
  );

  // The body is its own scroll container, so a row the consumer wants seen has to be scrolled to.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const lastScrolledRowId = useRef<string | number | null>(null);

  useLayoutEffect(() => {
    if (scrollToRowId === undefined || scrollToRowId === null || lastScrolledRowId.current === scrollToRowId) {
      return;
    }
    const selector = `[data-row-id="${CSS.escape(String(scrollToRowId))}"]`;
    const rowElement = scrollContainerRef.current?.querySelector(selector);
    // Absent means the row is not on the page currently rendered. A page index that has to move does so in
    // a later commit and re-runs this effect, so leaving the id unmarked lets that second pass finish it.
    if (!rowElement) {
      return;
    }
    lastScrolledRowId.current = scrollToRowId;
    // 'nearest' moves the row the shortest distance that puts it on screen, which keeps the page around the
    // grid from scrolling out from under the user too.
    rowElement.scrollIntoView({ block: 'nearest' });
  }, [scrollToRowId, data, pagination]);

  // Memoize checkbox header renderer
  const headerCheckboxRenderer = useCallback(
    (context: { table: Table<TData> }) => (
      <HeaderCheckbox table={context.table} customCheckbox={customHeaderCheckbox} />
    ),
    [customHeaderCheckbox],
  );

  // Memoize checkbox cell renderer
  const cellCheckboxRenderer = useCallback(
    (context: { row: Row<TData> }) => (
      <RowCheckbox row={context.row} disableSelectionOnClick={disableSelectionOnClick} />
    ),
    [disableSelectionOnClick],
  );

  // A cell function IS the React element type: flexRender calls createElement on it. Building a fresh
  // closure per column whenever the columns array changes therefore unmounts and remounts every cell, which
  // destroys the focus of anything the user is typing in. This one renderer stays referentially stable and
  // looks the consumer's column up through table meta, which TanStack refreshes on every render.
  const renderUserCell = useCallback((info: CellContext<TData, unknown>) => {
    const meta = info.table.options.meta as { userColumns?: UPTanStackTableColumn<TData>[] } | undefined;
    const col = meta?.userColumns?.find(candidate => candidate.id === info.column.id);
    const value = info.getValue();

    if (col?.cell) {
      return col.cell({ row: info.row.original, value });
    }

    return value !== null && value !== undefined ? String(value) : '';
  }, []);

  // Convert user columns to TanStack columns
  const columns = useMemo<ColumnDef<TData>[]>(() => {
    const cols: ColumnDef<TData>[] = [];

    // Add checkbox column if row selection is enabled
    if (shouldShowCheckboxes) {
      cols.push({
        id: 'select',
        size: 50,
        header: headerCheckboxRenderer,
        cell: cellCheckboxRenderer,
        meta: {
          align: 'center',
          headerAlign: 'center',
        },
      });
    }

    // Add user-defined columns
    userColumns.forEach((col: UPTanStackTableColumn<TData>) => {
      const columnDef: ColumnDef<TData> = {
        id: col.id,
        header: col.header,
        size: col.size,
        enableSorting: col.enableSorting ?? true,
        meta: {
          align: col.align || 'left',
          headerAlign: col.headerAlign || 'left',
          ...(col.colSpan && { colSpan: col.colSpan as (row: unknown) => number }),
        },
      };

      // Add accessorKey if provided
      if (col.accessorKey) {
        (columnDef as ColumnDef<TData> & { accessorKey: keyof TData }).accessorKey = col.accessorKey;
      }

      // Add cell renderer — always the shared stable one, which handles both a custom cell and the
      // default "print the accessor value" case.
      if (col.cell || col.accessorKey) {
        columnDef.cell = renderUserCell;
      }

      cols.push(columnDef);
    });

    return cols;
  }, [userColumns, shouldShowCheckboxes, headerCheckboxRenderer, cellCheckboxRenderer, renderUserCell]);

  // Create row selection state
  const rowSelection = useMemo(() => {
    const selection: Record<string, boolean> = {};
    selectedRows.forEach(id => {
      selection[String(id)] = true;
    });
    return selection;
  }, [selectedRows]);

  const table = useReactTable({
    data,
    columns,
    // Read by renderUserCell so it can resolve the current column without capturing it in a closure.
    meta: { userColumns },
    state: {
      sorting: enableSorting ? sorting : undefined,
      rowSelection,
      pagination: enablePagination ? pagination : undefined,
    },
    enableRowSelection: shouldShowCheckboxes,
    enableSorting,
    manualSorting,
    manualPagination,
    autoResetPageIndex,
    pageCount: manualPagination ? pageCount : undefined,
    onSortingChange: enableSorting ? setSorting : undefined,
    onPaginationChange: enablePagination ? setPagination : undefined,
    onRowSelectionChange: updater => {
      const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
      const selectedIds = Object.keys(newSelection)
        .filter(key => newSelection[key])
        .map(key => {
          // Try to convert to number if possible, otherwise keep as string
          const numKey = Number(key);
          return Number.isNaN(numKey) ? key : numKey;
        });
      onSelectionChange?.(selectedIds);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    getRowId: getRowId ? (row: TData) => String(getRowId(row)) : undefined,
  });

  const handleRowMouseEnter = useCallback(
    (rowId: string | number) => {
      setHoveredRowId(rowId);
      // Support both old and new API
      if (setRowOnHover) {
        setRowOnHover(Number(rowId));
      }
      onRowHover?.(rowId);
    },
    [onRowHover, setRowOnHover],
  );

  const handleRowMouseLeave = useCallback(() => {
    setHoveredRowId(null);
    if (setRowOnHover) {
      setRowOnHover(-1);
    }
    onRowHover?.(null);
  }, [onRowHover, setRowOnHover]);

  // Keyboard navigation for accessibility
  const handleHeaderKeyDown = useCallback((e: React.KeyboardEvent, header: Header<TData, unknown>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      header.column.getToggleSortingHandler()?.(e);
    }
  }, []);

  // Render header cell content
  const renderHeaderContent = useCallback((header: Header<TData, unknown>) => {
    if (header.isPlaceholder) {
      return null;
    }

    const isCheckboxColumn = header.column.id === 'select';

    if (isCheckboxColumn) {
      return flexRender(header.column.columnDef.header, header.getContext());
    }

    const meta = header.column.columnDef.meta as ColumnMeta;
    const alignMap = { left: 'flex-start', center: 'center', right: 'flex-end' } as const;
    const justifyContent = alignMap[meta?.headerAlign || 'left'];

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent }}>
        {flexRender(header.column.columnDef.header, header.getContext())}
        {{
          asc: <SortArrow />,
          desc: <SortArrow style={{ transform: 'rotate(180deg)' }} />,
        }[header.column.getIsSorted() as string] ?? null}
      </div>
    );
  }, []);

  // Render selection footer content
  const renderSelectionFooterContent = useCallback(() => {
    if (!renderSelectionFooter && !footerActions) {
      return null;
    }

    if (renderSelectionFooter) {
      return renderSelectionFooter(selectedRows, data);
    }

    if (footerActions) {
      return (
        <TableFooter
          selectedCount={footerSelectedCount ?? selectedRows.length}
          totalCount={footerTotalCount ?? data.length}
          actions={footerActions}
          clearAllText={footerClearAllText}
          selectedText={footerSelectedText}
          ofText={footerOfText}
          canClearAll={footerCanClearAll}
          extraText={footerExtraText}
          selectedCountTestId={footerSelectedCountTestId}
          onClearSelection={footerCanClearAll && onSelectionChange ? () => onSelectionChange([]) : undefined}
        />
      );
    }

    return null;
  }, [
    renderSelectionFooter,
    footerActions,
    selectedRows,
    data,
    footerClearAllText,
    footerSelectedText,
    footerOfText,
    footerCanClearAll,
    footerExtraText,
    footerSelectedCount,
    footerSelectedCountTestId,
    footerTotalCount,
    onSelectionChange,
  ]);

  // Render default mobile card if no custom renderer provided
  const renderDefaultMobileCard = useCallback(
    (row: TData, rowId: string | number, isSelected: boolean) => {
      const displayColumns = userColumns.filter(col => col.id !== 'select' && col.id !== 'actions');
      const actionsColumn = userColumns.find(col => col.id === 'actions');

      return (
        <MobileCard key={rowId} isSelected={isSelected} severity={getRowSeverity?.(row)}>
          {displayColumns.map(col => {
            let value: unknown;
            if (col.accessorKey) {
              value = row[col.accessorKey];
            }

            let displayValue: React.ReactNode;
            if (col.cell) {
              displayValue = col.cell({ row, value });
            } else if (value !== null && value !== undefined) {
              displayValue = String(value);
            } else {
              displayValue = '';
            }

            return (
              <MobileCardRow key={col.id}>
                <MobileCardLabel>{col.header}:</MobileCardLabel>
                <MobileCardValue>{displayValue}</MobileCardValue>
              </MobileCardRow>
            );
          })}
          {actionsColumn && (
            <MobileCardActions>
              {actionsColumn.cell ? actionsColumn.cell({ row, value: undefined }) : null}
            </MobileCardActions>
          )}
        </MobileCard>
      );
    },
    [userColumns, getRowSeverity],
  );

  if (data.length === 0) {
    return (
      <TableContainerStyled sx={sx} data-testid={testId}>
        <EmptyStateContainer>{emptyMessage}</EmptyStateContainer>
      </TableContainerStyled>
    );
  }

  // Without pagination the table holds no pagination state, and asking TanStack for a page count or the page
  // index throws. Everything then fits on one page holding every row.
  const paginationState = enablePagination ? table.getState().pagination : undefined;
  const totalPages = enablePagination ? table.getPageCount() : 1;
  const currentPage = paginationState ? paginationState.pageIndex + 1 : 1;

  // For manual pagination, use server-provided totalItems
  // For client-side pagination, use the filtered row model
  const totalRows = manualPagination && totalItems !== undefined ? totalItems : table.getFilteredRowModel().rows.length;

  const startRow = paginationState ? paginationState.pageIndex * paginationState.pageSize + 1 : 1;
  const endRow = paginationState ? Math.min(startRow + paginationState.pageSize - 1, totalRows) : totalRows;

  // Mobile Card View
  if (isMobile && renderMobileCard) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', ...sx }}>
        <MobileCardContainer>
          {table.getRowModel().rows.map(row => {
            const rowId = row.id;
            const isSelected = row.getIsSelected();
            const actionsColumn = userColumns.find(col => col.id === 'actions');
            const actions = actionsColumn?.cell ? actionsColumn.cell({ row: row.original, value: undefined }) : null;
            const checkbox = shouldShowCheckboxes ? (
              <Checkbox checked={isSelected} onChange={row.getToggleSelectedHandler()} size="medium" />
            ) : null;

            return (
              <Box key={rowId}>
                {renderMobileCard(row.original, actions, checkbox, isSelected, getRowSeverity?.(row.original))}
              </Box>
            );
          })}
        </MobileCardContainer>

        {(renderSelectionFooter || footerActions) && (
          <SelectionFooterContainer isVisible={showFooter}>{renderSelectionFooterContent()}</SelectionFooterContainer>
        )}

        {enablePagination && showPaginationControls && totalPages > 0 && (
          <PaginationContainer>
            <PaginationInfo>
              {t(PAGINATION__PAGE)} {currentPage} {t(PAGINATION__OF)} {totalPages}
            </PaginationInfo>
            <PaginationControls>
              <IconButton
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                size="small"
                aria-label="Previous page"
              >
                {'<'}
              </IconButton>
              <IconButton
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                size="small"
                aria-label="Next page"
              >
                {'>'}
              </IconButton>
            </PaginationControls>
          </PaginationContainer>
        )}
      </Box>
    );
  }

  // Mobile Default Card View (when no custom renderer)
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', ...sx }}>
        <MobileCardContainer>
          {table.getRowModel().rows.map(row => {
            const rowId = row.id;
            const isSelected = row.getIsSelected();
            const checkbox = shouldShowCheckboxes ? (
              <Checkbox checked={isSelected} onChange={row.getToggleSelectedHandler()} size="medium" />
            ) : null;

            return (
              <Box key={rowId}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>{renderDefaultMobileCard(row.original, rowId, isSelected)}</Box>
                  {checkbox && <Box sx={{ flexShrink: 0, pt: 2 }}>{checkbox}</Box>}
                </Box>
              </Box>
            );
          })}
        </MobileCardContainer>

        {(renderSelectionFooter || footerActions) && (
          <SelectionFooterContainer isVisible={showFooter}>{renderSelectionFooterContent()}</SelectionFooterContainer>
        )}

        {enablePagination && showPaginationControls && totalPages > 0 && (
          <PaginationContainer>
            <PaginationInfo>
              {t(PAGINATION__PAGE)} {currentPage} {t(PAGINATION__OF)} {totalPages}
            </PaginationInfo>
            <PaginationControls>
              <IconButton
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                size="small"
                aria-label="Previous page"
              >
                {'<'}
              </IconButton>
              <IconButton
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                size="small"
                aria-label="Next page"
              >
                {'>'}
              </IconButton>
            </PaginationControls>
          </PaginationContainer>
        )}
      </Box>
    );
  }

  // Desktop Table View
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', ...sx }}>
      <TableContainerStyled sx={{ flex: 1 }} ref={scrollContainerRef} data-testid={testId}>
        <StyledTable>
          <StyledThead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const meta = header.column.columnDef.meta as ColumnMeta;
                  const canSort = header.column.getCanSort();
                  return (
                    <StyledTh
                      key={header.id}
                      align={meta?.headerAlign || 'left'}
                      style={{ width: header.column.getSize(), cursor: canSort ? 'pointer' : 'default' }}
                      onClick={header.column.getToggleSortingHandler()}
                      onKeyDown={e => handleHeaderKeyDown(e, header)}
                      tabIndex={canSort ? 0 : -1}
                      role={canSort ? 'button' : undefined}
                      aria-sort={(() => {
                        const sortDirection = header.column.getIsSorted();
                        if (!sortDirection) return undefined;
                        return sortDirection === 'asc' ? 'ascending' : 'descending';
                      })()}
                    >
                      {renderHeaderContent(header)}
                    </StyledTh>
                  );
                })}
              </tr>
            ))}
          </StyledThead>
          <StyledTbody>
            {table.getRowModel().rows.map(row => {
              const rowId = row.id;
              const isHovered = hoveredRowId === rowId;
              const isSelected = row.getIsSelected();

              return (
                <StyledTr
                  key={row.id}
                  data-row-id={rowId}
                  data-testid={getRowTestId?.(row.original)}
                  isHovered={isHovered}
                  isSelected={isSelected}
                  severity={getRowSeverity?.(row.original)}
                  onMouseEnter={() => handleRowMouseEnter(rowId)}
                  onMouseLeave={handleRowMouseLeave}
                >
                  {(() => {
                    const cells = row.getVisibleCells();
                    const rendered: React.ReactNode[] = [];
                    let skipCount = 0;
                    cells.forEach(cell => {
                      if (skipCount > 0) {
                        skipCount -= 1;
                        return;
                      }
                      const meta = cell.column.columnDef.meta as ColumnMeta;
                      const span = meta?.colSpan ? meta.colSpan(row.original) : 1;
                      if (span > 1) skipCount = span - 1;
                      rendered.push(
                        <StyledTd key={cell.id} align={meta?.align || 'left'} colSpan={span > 1 ? span : undefined}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </StyledTd>,
                      );
                    });
                    return rendered;
                  })()}
                </StyledTr>
              );
            })}
          </StyledTbody>
        </StyledTable>
        {(renderSelectionFooter || footerActions) && (
          <SelectionFooterContainer isVisible={showFooter}>{renderSelectionFooterContent()}</SelectionFooterContainer>
        )}
      </TableContainerStyled>

      {enablePagination && showPaginationControls && totalPages > 0 && (
        <PaginationContainer>
          <PaginationInfo>
            {t(PAGINATION__SHOWING)} {startRow} {t(PAGINATION__TO)} {endRow} {t(PAGINATION__OF)} {totalRows}{' '}
            {t(PAGINATION__ROWS)}
            {pageSizeOptions && pageSizeOptions.length > 1 && (
              <Box component="span" sx={{ ml: 2 }}>
                <Select
                  value={pagination.pageSize}
                  onChange={e => {
                    const newPageSize = Number(e.target.value);
                    setPagination({ pageIndex: 0, pageSize: newPageSize });
                  }}
                  sx={{
                    fontSize: '14px',
                    '& .MuiSelect-select': {
                      padding: '4px 8px',
                    },
                  }}
                  variant="outlined"
                  size="small"
                >
                  {pageSizeOptions.map(size => (
                    <MenuItem key={size} value={size}>
                      {size} {t(PAGINATION__ROWS)}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}
          </PaginationInfo>
          <PaginationControls>
            <IconButton
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              size="small"
              aria-label="First page"
            >
              {'<<'}
            </IconButton>
            <IconButton
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              size="small"
              aria-label="Previous page"
            >
              {'<'}
            </IconButton>
            <Box sx={{ fontSize: '14px', color: '#212121', minWidth: '100px', textAlign: 'center' }}>
              {t(PAGINATION__PAGE)} {currentPage} {t(PAGINATION__OF)} {totalPages}
            </Box>
            <IconButton
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              size="small"
              aria-label="Next page"
            >
              {'>'}
            </IconButton>
            <IconButton
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
              size="small"
              aria-label="Last page"
            >
              {'>>'}
            </IconButton>
          </PaginationControls>
        </PaginationContainer>
      )}
    </Box>
  );
};

export default UPTanStackTable;
